import React, { useRef } from 'react';
import { Upload, X, FileText, Image as ImageIcon, File } from 'lucide-react';

interface FileAttachmentInputProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
}

const FileAttachmentInput: React.FC<FileAttachmentInputProps> = ({ 
  files, 
  onFilesChange,
  maxFiles = 5 
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <ImageIcon className="w-5 h-5 text-[#38BDF8]" />;
    if (type === 'application/pdf') return <FileText className="w-5 h-5 text-[#38BDF8]" />;
    return <File className="w-5 h-5 text-[#38BDF8]" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large (max 10MB)';
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Use PDF, JPG, PNG, DOC, or DOCX';
    }
    return null;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    addFiles(selectedFiles);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addFiles = (newFiles: File[]) => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of newFiles) {
      if (files.length + validFiles.length >= maxFiles) {
        errors.push(`Maximum ${maxFiles} files allowed`);
        break;
      }

      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    }

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      onFilesChange([...files, ...validFiles]);
    }
  };

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  return (
    <div className="space-y-3">
      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="border-2 border-dashed border-[#334155]/20 rounded-xl p-4 text-center hover:border-[#38BDF8]/50 hover:bg-[#F0F9FF]/50 transition-colors"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          multiple
          className="hidden"
          id="file-attachment-input"
        />
        
        <label
          htmlFor="file-attachment-input"
          className="cursor-pointer"
        >
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-8 h-8 text-[#38BDF8]" />
            <div>
              <p className="text-sm text-[#334155] font-medium">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-[#334155]/60 mt-1">
                PDF, JPG, PNG, DOC, DOCX (max 10MB, up to {maxFiles} files)
              </p>
            </div>
          </div>
        </label>
      </div>

      {/* Attached Files List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-[#F0F9FF] border border-[#38BDF8]/20 rounded-xl"
            >
              <div className="flex-shrink-0">
                {getFileIcon(file.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#334155] truncate">
                  {file.name}
                </p>
                <p className="text-xs text-[#334155]/60">
                  {formatFileSize(file.size)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="flex-shrink-0 p-1 hover:bg-[#FDA4AF]/20 rounded transition-colors"
              >
                <X className="w-4 h-4 text-[#334155]/60" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileAttachmentInput;
